import { Expense, RightEnum, Vendor, VendorTypeEnum } from "@finway-group/shared/lib/models"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"

import { patchExpense } from "Shared/store/actions/expense/expenseActions"

import { useEmployees } from "Shared/queries/employee.queries"
import { useCompany } from "../../../../../shared/hooks/company.hooks"
import { ApprovalProcessService, AuthzService, NotificationService } from "../../../../../shared/services"
import { buildUpdateDataAccordingToSetting, isHospitalityOrStandardReimbursement } from "../../../../../shared/utils/expense.utils"
import { UserCreditor } from "../../../../../shared/utils/vendor.utils"
import { VendorField, VendorFieldProps } from "./VendorField"

interface ExpenseVendorFieldProps {
    expense: Expense
    setIsExpenseUpdating?: React.Dispatch<React.SetStateAction<boolean>>
    setSelectedExpense?: (expense: Expense | undefined) => void
    currentExpenseCreditor: UserCreditor | Vendor | undefined
    vendorCreditor?: Vendor
    isReadOnly?: boolean
    areInputsDisabled: boolean
}

export const ExpenseVendorField = ({
    expense,
    currentExpenseCreditor,
    isReadOnly,
    vendorCreditor,
    setIsExpenseUpdating,
    areInputsDisabled,
    setSelectedExpense,
}: ExpenseVendorFieldProps) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const company = useCompany()
    const { data: employees } = useEmployees({ excludeDeleted: true })

    const [isVendorUpdating, setIsVendorUpdating] = useState(false)

    const isUserCreditor = expense?.vendorType === VendorTypeEnum.USER
    const embeddedExpenseCreditor = expense.getCreditor()

    const onVendorUpdate = async (value: string, vendorType: VendorTypeEnum = VendorTypeEnum.VENDOR) => {
        try {
            setIsVendorUpdating(true)
            setIsExpenseUpdating?.(true)

            // Assign new vendor value to the expense object
            const updatedExpense = new Expense({
                ...expense,
                ...(vendorType === VendorTypeEnum.USER
                    ? {
                          vendorType: VendorTypeEnum.USER,
                          creditorUser: { _id: value },
                          vendor: undefined,
                      }
                    : {
                          vendorType: VendorTypeEnum.VENDOR,
                          vendor: { _id: value },
                          creditorUser: undefined,
                      }),
                skipEmail: true,
            })

            // Determine the cc, cc2, expenseaccount, etc, based on the newly updated expense
            const extraUpdateData = buildUpdateDataAccordingToSetting(updatedExpense, employees!, company, vendorCreditor)
            Object.assign(updatedExpense, extraUpdateData)

            // Rebuild approval process with vendor change. Expenses inside folder do not change its aproval processes and follow the folder approval processes.
            if (!updatedExpense.folderId) {
                const approvalProcesses = await ApprovalProcessService.getApprovalProcessesAfterExpenseUpdate(updatedExpense, company, {
                    valuesChange: { vendor: { previousValue: expense.vendor?._id, currentValue: value } },
                    awaitConfirmation: true,
                })
                if (!approvalProcesses) {
                    return
                }
                updatedExpense.approvalProcesses = approvalProcesses
            }

            const patchPayload = {
                vendor: updatedExpense.vendor,
                vendorType: updatedExpense.vendorType,
                creditorUser: updatedExpense.creditorUser,
                approvalProcesses: updatedExpense.approvalProcesses,
                ...extraUpdateData,
            }

            const updatedExpenseResponse = await patchExpense(expense.id, patchPayload)(dispatch)
            setSelectedExpense?.(updatedExpenseResponse)
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:error"))
        } finally {
            setIsVendorUpdating(false)
            setIsExpenseUpdating?.(false)
        }
    }

    const vendorFieldProps: VendorFieldProps = {
        currentCreditor: currentExpenseCreditor,
        embeddedCreditor: embeddedExpenseCreditor,
        isUserCreditor,
        selectedCreditorId: currentExpenseCreditor ? currentExpenseCreditor.id : "",
        selectedVendorType: expense?.vendorType === VendorTypeEnum.USER && currentExpenseCreditor ? VendorTypeEnum.USER : VendorTypeEnum.VENDOR,
        shouldRenderSelectDropdown: isHospitalityOrStandardReimbursement(expense),
        onVendorUpdate,
        isReadOnly,
        areInputsDisabled,
        isLoading: isVendorUpdating,
        showUserCreditors: AuthzService.isRightGrantedForLoggedInUser(RightEnum.EMPLOYEE__ALL__READ),
    }

    return <VendorField {...vendorFieldProps} />
}
